/*
 * Whether a patient is pregnant right now, and how far along (KIN-1941).
 *
 * This used to be inferred from the estimated due date alone, because there was no status to
 * consult. That produced two faults from one cause: the banner vanished once the due date passed,
 * exactly when a post-dates pregnancy is most worth flagging to whoever opens the chart, and it
 * went on announcing a gestation for weeks after a delivery, a miscarriage or a termination.
 *
 * KIN-1940 added completion, so the question is now answerable directly: a pregnancy exists and is
 * not completed. Gestation stays derived from the due date, but is allowed to run past term.
 */
import { isPregnancyCompleted } from "./pregnancySidebarEntry";

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
const FULL_TERM_DAYS = 280;

/**
 * The pregnancy the patient is currently in, or null.
 *
 * Where more than one is open, this takes the most recently created — the same rule the backend's
 * `findCurrentPregnancyId` uses to decide where inbound hospital messages land, so the banner
 * cannot describe one pregnancy while the messages attach to another. Whether a second pregnancy
 * should be openable at all is KIN-2008's question, not this one's.
 */
export const selectCurrentPregnancy = (pregnancies) => {
  if (!Array.isArray(pregnancies)) return null;

  return (
    pregnancies
      .filter((pregnancy) => !!pregnancy && !isPregnancyCompleted(pregnancy))
      .sort((left, right) => (right?.id ?? 0) - (left?.id ?? 0))[0] ?? null
  );
};

/**
 * Gestation from the estimated due date: full term less the days still to go.
 *
 * Deliberately not capped at the due date. A pregnancy at 41 or 42 weeks is still a pregnancy, and
 * returning nothing for it is what made the banner disappear when it mattered most.
 */
export const getGestation = (estimatedDueDate, now = Date.now()) => {
  if (!estimatedDueDate) return null;

  const dueDate = new Date(estimatedDueDate);

  if (Number.isNaN(dueDate.getTime())) return null;

  const daysRemaining = Math.round((dueDate.getTime() - now) / MILLISECONDS_PER_DAY);
  const totalDays = FULL_TERM_DAYS - daysRemaining;

  // A due date more than nine months away describes no gestation that has started yet; showing a
  // negative one would be worse than showing nothing.
  if (totalDays < 0) return null;

  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
};

const pluralise = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * The gestation as a GP would say it: "29 weeks and 1 day".
 *
 * The banner renders this inside "Currently … pregnant", so it is read as a sentence rather than as
 * a pair of numbers — which is why it carries an "and" and why both halves are pluralised. Without
 * the second, the separator on its own would produce "29 weeks and 1 days".
 *
 * A whole number of weeks stops there: "29 weeks and 0 days" states a precision nobody asked for.
 */
export const formatGestation = (gestation) => {
  if (!gestation) return null;

  const weeks = pluralise(gestation.weeks, "week");

  return gestation.days > 0
    ? `${weeks} and ${pluralise(gestation.days, "day")}`
    : weeks;
};
